import { useMemo, useState, useCallback } from 'react';
import { compose } from 'redux';
import { connect, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { JSXElementConstructor } from 'react';

import { programSelectors } from '../../../models/program';
import { getChannelById } from '../../../models/channels/selectors';
import ViewTrigger from '../../../components/view-trigger';

import ContentLayoutGallery from '../../../components/v2/content-layouts/content-layout-gallery';
import ContentLayoutRow from '../../../components/v2/content-layouts/content-layout-row';
import ContentLayoutGrid from '../../../components/v2/content-layouts/content-layout-grid';
import ContentLayoutHero from '../../../components/v2/content-layouts/content-layout-hero';
import { DEFAULT_SKIP_TO_CONTENT_ID } from '../../../components/skip-to-content/skip-to-content';
import withRerenderPrevent from '../../../common/with-rerender-prevent';
import {
  HomeFeedSection as HomeFeedSectionType,
  useHomeFeedSectionsQuery,
} from '../../../common/queries/use-home-feed-sections-query';
import {
  InitiativeTagResponse,
  useInitiativeTagsQuery,
} from '../../../common/queries/use-initiative-tags-query';
import {
  useContentFeedsBySectionsQuery,
  ContentFeedSectionConfig,
} from '../../../common/queries/use-content-feed-query';
import { Program } from '../../../models/program/types';
import { Content } from '../../../models/content/types';
import { Channel } from '../../../models/channels/types';
import { RootPatronState } from '../../../common/use-patron-selector';

/**
 * Props for the HomeFeedSection component
 */
type HomeFeedSectionProps = {
  item: HomeFeedSectionType;
  className: string;
  feedItems: Content[];
  isLoading: boolean;
  isLoaded: boolean;
  onFetch: () => void;
  initiativeTags: InitiativeTagResponse[];
};

/**
 * Max display contents configuration
 */
type MaxDisplayContents = {
  hero: number;
  grid: number;
  gallery: number;
  row: number;
};

const MAX_DISPLAY_CONTENTS_MAP: MaxDisplayContents = {
  hero: 4,
  grid: 6,
  gallery: 9,
  row: 3,
};

/**
 * HomeFeedSection component that renders a single feed section
 */
const HomeFeedSection = ({
  item,
  className,
  feedItems,
  isLoading,
  isLoaded,
  onFetch,
  initiativeTags,
}: HomeFeedSectionProps): JSX.Element | null => {
  const {
    filter_id: filterId,
    display_type: displayType,
    feed,
    order,
    min_contents: minContents,
  } = item.attributes;

  const { t } = useTranslation();

  const manualFetch = order > 1;
  const channel = useSelector((state: RootPatronState) =>
    getChannelById(state, filterId)
  ) as Channel | undefined;

  // Find matching initiative tag by converting filterId to string for comparison
  const initiative = initiativeTags.find(
    (tag) => tag.id === String(filterId)
  )?.attributes;

  const displayTitle = (): string => {
    if (feed === 'channel') return channel?.name || '';
    if (feed === 'initiative') return initiative?.name || '';
    return t(`landing.${feed}`, feed);
  };

  const displayDescription = (): string | undefined => {
    if (feed === 'channel') return channel?.description;
    if (feed === 'initiative') return undefined;
    return t(`landing.${feed}_description`);
  };

  const getSectionId = (): string => {
    if (feed === 'channel' && channel) return `channel-${channel.id}`;
    if (feed === 'initiative' && initiative) {
      return `initiative-${initiative.id}`;
    }
    return feed;
  };

  const viewAllText = t('landing.see_all');
  const viewAllContext = displayTitle();

  const viewAllUrl = (): string => {
    if (feed === 'channel') return `/channels/${filterId}`;
    if (feed === 'initiative') return `/initiatives/${filterId}`;
    return `/feed/${feed}`;
  };

  // Limit feedItems to maxDisplayContents for this section
  const contents = useMemo(() => {
    const maxDisplayContents = MAX_DISPLAY_CONTENTS_MAP[displayType];
    return feedItems.slice(0, maxDisplayContents);
  }, [feedItems, displayType]);

  const shouldDisplay = (): boolean => {
    // The home feed component assumes an array of content with length > 1
    // Adding defensive check for uninitialized data coming from server
    let result = contents.length >= (minContents || 1);

    if (feed === 'initiative') {
      result = result && initiative !== undefined && initiative.is_active;
    }

    if (feed === 'channel') {
      result = result && channel !== undefined;
    }

    return result;
  };

  // Eventually we need to clean up these class names
  // Some of these don't seem to have any CSS defined against them
  const classNames = {
    hero: 'home__featured-v2',
    grid: 'home__digests',
    gallery: 'home__community-v2',
    row: 'home__row-v2',
  };

  // For sections beyond the first, show ViewTrigger for lazy loading
  if (manualFetch && !isLoaded) {
    return <ViewTrigger offset="200px" onInview={onFetch} />;
  }

  if (!shouldDisplay()) {
    return null;
  }

  // Build base common props for all feed components
  const baseProps = {
    id: getSectionId(),
    title: displayTitle(),
    description: displayDescription(),
    seeAllText: viewAllText,
    viewAllText,
    viewAllContext,
    feedUrl: viewAllUrl(),
    contents: contents,
    analyticsData: { location: `home_${feed}` },
    isTopic: feed === 'channel',
  };

  return (
    <section
      id={DEFAULT_SKIP_TO_CONTENT_ID}
      className={cx(classNames[displayType], className)}
      aria-labelledby={`${baseProps.id}--title`}
      aria-describedby={
        baseProps.description ? `${baseProps.id}--description` : undefined
      }
    >
      {/* This page has no edge-to-edge/mobile container variant, so mobile-web
      layouts always stay off here regardless of the flags' global state. */}
      <div className="container">
        {displayType === 'gallery' && <ContentLayoutGallery {...baseProps} />}
        {displayType === 'hero' && (
          <ContentLayoutHero
            {...baseProps}
            mobileWebLayouts={false}
            mobileWebParity={false}
          />
        )}
        {displayType === 'grid' && (
          <ContentLayoutGrid
            {...baseProps}
            minLength={minContents || 1}
            maxLength={6}
            isFetching={isLoading}
            mobileWebLayouts={false}
            mobileWebParity={false}
          />
        )}
        {displayType === 'row' && (
          <ContentLayoutRow
            {...baseProps}
            minLength={minContents || 1}
            isFetching={isLoading}
            mobileWebLayouts={false}
            mobileWebParity={false}
          />
        )}
      </div>
    </section>
  );
};

/**
 * Deduplicates content arrays based on content IDs
 *
 * @param contentsArr - Array of content arrays to deduplicate
 * @param lengthArr - Array of max lengths for each content array
 * @returns Deduplicated array of content arrays
 */
const dedupContents = (
  contentsArr: Content[][],
  lengthArr: number[]
): Content[][] => {
  const flattenContentIds = (contents: Content[][]) =>
    Array.prototype.concat
      .apply([] as Content[], contents)
      .map((c: Content) => c.id);

  return contentsArr.reduce((arr, contents, idx) => {
    const curIds = flattenContentIds(arr);
    const uniqContents = contents
      .filter((c) => !curIds.includes(c.id))
      .slice(0, lengthArr[idx]);

    return [...arr, uniqContents];
  }, [] as Content[][]);
};

/**
 * Props for the HomeFeeds component
 */
type HomeFeedsProps = {
  inCaseYouMissedItEnabled?: boolean;
  fromTheCommunityEnabled?: boolean;
  forYouFeedEnabled?: boolean;
  program?: Program;
};

/**
 * HomeFeeds is the main component that displays all feed sections on the home page
 */
const HomeFeeds = ({
  inCaseYouMissedItEnabled = false,
  fromTheCommunityEnabled = false,
  forYouFeedEnabled = false,
}: HomeFeedsProps): JSX.Element | null => {
  // Use React Query to fetch feed sections
  const { data: feedSections = [], isLoading: isFetchingFeedSections } =
    useHomeFeedSectionsQuery();

  // Track which sections should have their queries enabled
  // Start with first two sections enabled (order 0 and 1)
  const [enabledSections, setEnabledSections] = useState<Set<number>>(
    new Set([0, 1])
  );

  // Extract filter IDs for initiative tags query
  const filterIds = useMemo(() => {
    return feedSections
      .map((section) => section.attributes?.filter_id)
      .filter(Boolean)
      .map(String);
  }, [feedSections]);

  // Use React Query to fetch initiative tags
  const { data: initiativeTagsData = [] } = useInitiativeTagsQuery(
    filterIds.length > 0 ? filterIds : undefined
  );

  // Filter feed sections based on display flags
  const visibleFeedSections = feedSections.filter((section) => {
    const feed = section.attributes.feed;
    if (feed === 'community') return fromTheCommunityEnabled;
    if (feed === 'icymi') return inCaseYouMissedItEnabled;
    if (feed === 'for_you') return forYouFeedEnabled;
    return true;
  });

  // Prepare query configs for all feed sections
  const feedSectionConfigs = useMemo<ContentFeedSectionConfig[]>(() => {
    return visibleFeedSections.map((section, idx) => {
      const {
        filter_id: filterId,
        display_type: displayType,
        feed,
        order,
      } = section.attributes;

      // Only enable queries for sections in enabledSections
      const enabled = enabledSections.has(idx);

      return {
        feed,
        filterId,
        displayType,
        order,
        enabled,
      };
    });
  }, [visibleFeedSections, enabledSections]);

  // Fetch all module feeds in parallel using the new hook
  const feedQueries = useContentFeedsBySectionsQuery(feedSectionConfigs);

  // Handler to enable a section's query when it's triggered
  const handleSectionFetch = useCallback((idx: number, refetch: () => void) => {
    // Enable the query for this section
    setEnabledSections((prev) => new Set(prev).add(idx));
    // Refetch will be called automatically when enabled changes,
    // but we can also call it explicitly for immediate fetch
    refetch();
  }, []);

  // Deduplicate contents across sections
  const allFeedItems = feedQueries.map((query) => query.data || []);
  const maxContents = visibleFeedSections.map((section) => {
    const displayType = section.attributes.display_type;
    return MAX_DISPLAY_CONTENTS_MAP[displayType];
  });

  const deduplicatedContents = dedupContents(allFeedItems, maxContents);

  if (isFetchingFeedSections) {
    return null;
  }

  return (
    <div className="home__feed-sections">
      {visibleFeedSections.map((feed_section, idx) => {
        const query = feedQueries[idx];
        return (
          <HomeFeedSection
            key={idx}
            item={feed_section}
            className={cx('home_row', {
              home__first: idx === 0,
            })}
            feedItems={deduplicatedContents[idx] || []}
            isLoading={query.isLoading}
            isLoaded={enabledSections.has(idx) && query.isSuccess}
            onFetch={() => handleSectionFetch(idx, query.refetch)}
            initiativeTags={initiativeTagsData}
          />
        );
      })}
    </div>
  );
};

/**
 * Maps Redux state to component props
 */
const mapStateToProps = (state: RootPatronState) => ({
  inCaseYouMissedItEnabled:
    programSelectors.getProgramInCaseYouMissedItEnabled(state),
  fromTheCommunityEnabled:
    programSelectors.getProgramFromTheCommunityEnabled(state),
  forYouFeedEnabled: programSelectors.getForYouFeedEnabled(state),
  program: programSelectors.getProgram(state),
});

export default compose<JSXElementConstructor<HomeFeedsProps>>(
  connect(mapStateToProps, null),
  withRerenderPrevent
)(HomeFeeds);
